import React from 'react'

// Constants
import { CANVAS_VIEW_BETA_DOCS } from '../../constants'

// Components
import { PageCanvasError } from 'modules/shares/PageCanvasView/components/PageCanvasError'
import { useWebRendererSupport } from 'modules/shares/components/WebRenderer/utils/hooks/useWebRendererSupport'

// Types
type PageCanvasSupportGateProps = {
  children: React.ReactElement
}

/**
 * Handles showing alternative content if the Web Renderer is not supported.
 */
export function PageCanvasSupportGate({
  children,
}: PageCanvasSupportGateProps) {
  const {
    supported,
    detail: { viewport, webGL, wasm },
  } = useWebRendererSupport()

  if (supported) return children

  if (!viewport)
    return (
      <PageCanvasError
        title="Canvas view not available for mobile"
        description="Canvas view isn’t available for mobile devices yet. Try using a desktop or tablet browser instead."
        showViewAsArtboardsAction
      />
    )

  if (!webGL)
    return (
      <PageCanvasError
        title="Canvas view not available"
        description={
          <>
            Canvas view requires WebGL, but it‘s either disabled or not
            supported by your browser.{' '}
            <a href={CANVAS_VIEW_BETA_DOCS}>Learn more</a> about the canvas view
            beta.
          </>
        }
        showViewAsArtboardsAction
      />
    )

  if (!wasm)
    return (
      <PageCanvasError
        title="Canvas view not available"
        description={
          <>
            Canvas view requires WebAssembly, but it‘s either disabled or not
            supported by your browser.{' '}
            <a href={CANVAS_VIEW_BETA_DOCS}>Learn more</a> about the canvas view
            beta.
          </>
        }
        showViewAsArtboardsAction
      />
    )

  // Note: No real chance of rendering `null` here, as long as all scenarios are
  // handled above. Just added for an explicit return type.
  return null
}
